import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, Radar } from "lucide-react";
import { Button } from "./ui/button";
import axios, { API } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const TIER_STYLE = {
  signal: { border: "border-surface-border", accent: "text-slate-300", btn: "bg-white text-obsidian hover:bg-slate-200" },
  sentinel: { border: "border-openai-cyan/50", accent: "text-openai-cyan", btn: "bg-openai-cyan text-obsidian hover:bg-openai-cyan/90" },
  director: { border: "border-sovereign/60", accent: "text-sovereign", btn: "bg-sovereign text-white hover:bg-sovereign/90" },
  sovereign: { border: "border-[#D4AF37]/60", accent: "text-[#D4AF37]", btn: "bg-[#D4AF37] text-obsidian hover:bg-[#D4AF37]/90" },
};
const TIER_ORDER = ["signal", "sentinel", "director", "sovereign"];

export const SubscriptionPricing = () => {
  const [tiers, setTiers] = useState(null);
  const [loadingTier, setLoadingTier] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/subscriptions/tiers`).then((res) => setTiers(res.data)).catch(() => {});
  }, []);

  const select = async (tierId) => {
    if (tierId === "signal") {
      navigate(user ? "/dashboard" : "/register");
      return;
    }
    if (!user) {
      navigate("/register");
      return;
    }
    setLoadingTier(tierId);
    try {
      const { data } = await axios.post(`${API}/subscriptions/checkout`, { tier: tierId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Could not start checkout.");
      setLoadingTier(null);
    }
  };

  if (!tiers) return null;
  const byId = Object.fromEntries(tiers.map((t) => [t.id, t]));

  return (
    <div className="mx-auto max-w-6xl px-6 py-20" data-testid="subscription-pricing-section">
      <div className="mb-12 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-surface-border bg-surface-card px-4 py-1.5">
          <Radar className="h-3.5 w-3.5 text-sovereign" />
          <span className="text-xs font-medium uppercase tracking-widest text-slate-400">Sovereign Pulse Plans</span>
        </div>
        <h2 className="font-heading text-3xl font-extrabold text-white sm:text-4xl">Continuous reputation monitoring, on autopilot</h2>
        <p className="mx-auto mt-3 max-w-2xl text-base text-slate-400">
          Monitor your name or business across AI models every month &mdash; start free, upgrade when the stakes get higher.
        </p>
      </div>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {TIER_ORDER.map((id) => {
          const t = byId[id];
          if (!t) return null;
          const style = TIER_STYLE[id];
          return (
            <div key={id} className={`flex flex-col rounded-lg border ${style.border} bg-surface-card p-6`} data-testid={`subscription-tier-card-${id}`}>
              <h3 className={`font-heading text-lg font-bold ${style.accent}`}>{t.name}</h3>
              <p className="mb-4 font-heading text-3xl font-extrabold text-white">
                {t.price_cents === 0 ? "Free" : `$${(t.price_cents / 100).toFixed(0)}`}
                {t.price_cents > 0 && <span className="text-sm font-normal text-slate-500">/mo</span>}
              </p>
              <ul className="mb-6 flex-1 space-y-2">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-truth-green" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => select(id)}
                disabled={loadingTier === id}
                className={`w-full gap-2 ${style.btn}`}
                data-testid={`subscription-select-${id}-btn`}
              >
                {loadingTier === id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {id === "signal" ? "Get Started Free" : `Choose ${t.name}`}
              </Button>
            </div>
          );
        })}
      </div>
      <p className="mt-8 text-center text-sm text-slate-500">
        Need a one-time brand audit instead?{" "}
        <a href="#free-audit" className="text-openai-cyan hover:underline" data-testid="one-time-audit-link">
          Run a free Pulse Check audit
        </a>{" "}
        and unlock Fix Manual, Truth Verifier, Agency, or Sovereign Defense tiers à la carte.
      </p>
    </div>
  );
};
